import { YesNo } from '@fruitful/types';
import { capitalize } from 'lodash';

export const isEmpty = <T>(value: T): boolean => {
    return (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        value === '' ||
        Object.keys(value as object).length === 0
    );
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    value != null && typeof value === 'object' && !Array.isArray(value);

export const getOrdinalSuffixedNumber = (num: number): string => {
    const i = num % 10;
    const j = num % 100;

    if (i === 1 && j !== 11) {
        return `${num}st`;
    }

    if (i === 2 && j !== 12) {
        return `${num}nd`;
    }

    if (i === 3 && j !== 13) {
        return `${num}rd`;
    }

    return `${num}th`;
};

// Transforms enum values to be display values for UI,
// by accepting a string with underscores as token delimiters,
// and returns a title-cased string with spaces as delimiters.
// Example input: MARRIED_FILING_JOINTLY
// Returns: 'Married filing jointly'
export const transformEnumToDisplay = (input: string): string => {
    // Split the input string by underscores.
    const words = input.split('_');

    // Capitalize the first word and keep the rest in lowercase.
    const formattedWords = words.map((word, index) => {
        if (index === 0) {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }

        return word.toLowerCase();
    });

    return formattedWords.join(' ');
};

export const transformEnumToLabelValueOption = (
    obj: Record<string, string>,
): Array<{ label: string; value: string }> =>
    Object.values(obj).map((value) => ({
        label: capitalize(value.replace(/_/g, ' ')),
        value,
    }));

export const convertYesNoToBoolean = (value?: YesNo): boolean | undefined =>
    value != null ? value === YesNo.YES : undefined;

export const convertBooleanToYesNo = (
    bool: boolean | null | undefined,
): YesNo | undefined =>
    bool != null ? (bool ? YesNo.YES : YesNo.NO) : undefined;

export const castToDecimal = (val: number | string): number =>
    typeof val === 'string' ? parseFloat(val) : val;

export const formatDecimalInput = (value: string): string => {
    // remove all invalid characters
    let cleaned = value.replace(/[^0-9.]/g, '');

    // only keep a single dot
    const parts = cleaned.split('.');
    if (parts.length > 2) {
        cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    // leading zero logic
    if (
        cleaned.startsWith('0') &&
        cleaned.length > 1 &&
        !cleaned.startsWith('0.')
    ) {
        // strip the leading zero unless it's a decimal like "0.25"
        cleaned = cleaned.replace(/^0+/, '');
    }

    // if starts with '.', prepend '0'
    if (cleaned.startsWith('.')) {
        cleaned = `0${cleaned}`;
    }

    // limit to 2 decimal places
    const match = /^(\d+)(\.\d{0,2})?/.exec(cleaned);
    return match ? match[1] + (match[2] ?? '') : '';
};

// Also duplicated in packages/money-map-simulator/src/withArticle.ts — keep in sync.
// money-map-simulator cannot import @fruitful/fruitils in its Node build (see that file).
const ARTICLE_SKIP_PREFIXES = [
    'a ',
    'an ',
    'the ',
    'my ',
    'your ',
    'our ',
    'their ',
];

/** Descriptions that are mass nouns, plurals, or gerunds and shouldn't get "a/an". */
const NO_ARTICLE_WORDS = new Set([
    'travel',
    'gifts',
    'education',
    'moving',
    'home repairs',
    'charitable giving',
    'growing a family',
]);

export const needsArticle = (name: string): boolean => {
    const lower = name.toLowerCase();
    if (NO_ARTICLE_WORDS.has(lower)) return false;
    return !ARTICLE_SKIP_PREFIXES.some((prefix) => lower.startsWith(prefix));
};

export const withArticle = (name: string): string => {
    const lower = name.charAt(0).toLowerCase() + name.slice(1);
    return needsArticle(lower) ? `a ${lower}` : lower;
};

/**
 * Lowercases only the first character, preserving the casing of proper nouns
 * and other words. Useful for embedding a sentence-cased phrase mid-sentence.
 */
export const lowercaseFirst = (text: string): string =>
    text.length === 0
        ? text
        : `${text.charAt(0).toLowerCase()}${text.slice(1)}`;

export const makePossessive = (
    value?: string | null,
): string | undefined | null => {
    if (value == null) return value;

    return `${value}'${value.endsWith('s') ? '' : 's'}`;
};
